/**
 * GREAT WORKS UTILITIES - Cluster detection and validation
 *
 * Performance optimizations:
 * - Results are cached and only recalculated when tiles change
 * - Uses Set for O(1) lookups
 * - Early exit when requirements are met
 * - Lazy evaluation - only checks requirements when needed
 */

import { idToCoords, coordsToId, hexDistance, getAdjacentCoords } from '../../utils/hexGrid';
import { biomeMatchesRequirement, getBiomesMatchingRequirement } from './greatWorksCategories';
import { GREAT_WORKS } from './greatWorks';

// ===========================================
// CACHE MANAGEMENT
// ===========================================

let validPlacementsCache = null;
let cacheKey = null;

/**
 * Clear the placement cache (call when tiles change)
 */
export function clearGreatWorksCache() {
  validPlacementsCache = null;
  cacheKey = null;
}

/**
 * Generate a cache key from discovered nodes
 * Uses tile count + hash of node IDs for quick invalidation check
 */
function generateCacheKey(discoveredNodes) {
  const nodeIds = Object.keys(discoveredNodes).sort();
  const count = nodeIds.length;
  // Simple hash: combine first few and last few node IDs
  const sample = nodeIds.slice(0, 3).concat(nodeIds.slice(-3)).join('|');
  return `${count}:${sample}`;
}

// ===========================================
// CORE CLUSTER DETECTION
// ===========================================

/**
 * Get all tiles within a certain hex distance from a point
 * @param {number} centerQ - Center Q coordinate
 * @param {number} centerR - Center R coordinate
 * @param {number} range - Max distance in hexes
 * @param {Object} discoveredNodes - Map of discovered tiles
 * @returns {Array<{nodeId: string, q: number, r: number, biome: string}>}
 */
export function getTilesInRange(centerQ, centerR, range, discoveredNodes) {
  const tiles = [];

  for (const [nodeId, node] of Object.entries(discoveredNodes)) {
    if (nodeId === 'kingdom') continue; // Skip kingdom

    const coords = idToCoords(nodeId);
    if (!coords) continue;

    const distance = hexDistance(centerQ, centerR, coords.q, coords.r);
    if (distance > 0 && distance <= range) {
      tiles.push({
        nodeId,
        q: coords.q,
        r: coords.r,
        biome: node.biome,
        distance,
      });
    }
  }

  return tiles;
}

/**
 * Get connected tiles starting from a point using flood fill
 * @param {number} startQ - Start Q coordinate
 * @param {number} startR - Start R coordinate
 * @param {Object} discoveredNodes - Map of discovered tiles
 * @param {function} matchFn - Function to check if a tile should be included
 * @param {number} maxRange - Maximum range to search (performance limit)
 * @returns {Set<string>} Set of connected node IDs
 */
export function getConnectedTiles(startQ, startR, discoveredNodes, matchFn, maxRange = 5) {
  const connected = new Set();
  const visited = new Set();
  const queue = [{ q: startQ, r: startR }];

  while (queue.length > 0) {
    const { q, r } = queue.shift();
    const nodeId = coordsToId(q, r);

    // Skip if already visited or too far
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    // Check distance limit
    if (hexDistance(startQ, startR, q, r) > maxRange) continue;

    // Check if this tile exists and matches
    const node = discoveredNodes[nodeId];
    if (!node || !matchFn(node.biome)) continue;

    connected.add(nodeId);

    // Add adjacent tiles to queue
    for (const adj of getAdjacentCoords(q, r)) {
      const adjId = coordsToId(adj.q, adj.r);
      if (!visited.has(adjId)) {
        queue.push(adj);
      }
    }
  }

  return connected;
}

/**
 * Count unique biome types in a set of tiles
 * @param {Array} tiles - Array of tile objects with biome property
 * @returns {number} Number of unique biome types
 */
function countUniqueBiomes(tiles) {
  const biomes = new Set(tiles.map(t => t.biome));
  return biomes.size;
}

// ===========================================
// REQUIREMENT VALIDATION
// ===========================================

/**
 * Check if a tile meets the anchor requirement for a Great Work
 * @param {string} biomeId - The biome of the tile
 * @param {Object} anchorReq - The anchor requirement from Great Work
 * @returns {boolean}
 */
export function meetsAnchorRequirement(biomeId, anchorReq) {
  if (!anchorReq) return true;

  // Check specific biomes
  if (anchorReq.biomes) {
    return anchorReq.biomes.includes(biomeId);
  }

  // Check category
  if (anchorReq.category) {
    return biomeMatchesRequirement(biomeId, anchorReq.category);
  }

  return true;
}

/**
 * Check if nearby tiles meet the nearby requirement for a Great Work
 * @param {number} anchorQ - Anchor tile Q
 * @param {number} anchorR - Anchor tile R
 * @param {Object} nearbyReq - The nearby requirement from Great Work
 * @param {Object} discoveredNodes - Map of discovered tiles
 * @returns {{meets: boolean, tiles: Array, count: number}}
 */
export function checkNearbyRequirement(anchorQ, anchorR, nearbyReq, discoveredNodes) {
  if (!nearbyReq) {
    return { meets: true, tiles: [], count: 0 };
  }

  const range = nearbyReq.range || 2;
  const minCount = nearbyReq.minCount || 1;
  const requireConnected = nearbyReq.connected || false;
  const requireDiversity = nearbyReq.requireDiversity || 0;

  // Get all tiles in range
  let tilesInRange = getTilesInRange(anchorQ, anchorR, range, discoveredNodes);

  // Filter by biome requirement
  if (!nearbyReq.anyBiome) {
    const requirement = nearbyReq.category || nearbyReq.biomes;
    tilesInRange = tilesInRange.filter(t =>
      biomeMatchesRequirement(t.biome, requirement)
    );
  }

  // If connected required, filter to only connected tiles
  if (requireConnected && tilesInRange.length >= minCount) {
    const matchFn = nearbyReq.anyBiome
      ? () => true
      : (biome) => biomeMatchesRequirement(biome, nearbyReq.category || nearbyReq.biomes);

    const connectedSet = getConnectedTiles(anchorQ, anchorR, discoveredNodes, matchFn, range);

    // Filter to only tiles that are connected
    tilesInRange = tilesInRange.filter(t => connectedSet.has(t.nodeId));
  }

  // Check diversity requirement
  if (requireDiversity > 0) {
    const uniqueCount = countUniqueBiomes(tilesInRange);
    if (uniqueCount < requireDiversity) {
      return { meets: false, tiles: tilesInRange, count: tilesInRange.length, diversity: uniqueCount };
    }
  }

  const meets = tilesInRange.length >= minCount;

  return {
    meets,
    tiles: tilesInRange,
    count: tilesInRange.length,
    required: minCount,
    diversity: countUniqueBiomes(tilesInRange),
  };
}

/**
 * Check if a specific tile can be an anchor for a Great Work
 * @param {string} nodeId - The node ID to check
 * @param {string} workId - The Great Work ID
 * @param {Object} discoveredNodes - Map of discovered tiles
 * @returns {{valid: boolean, nearbyCheck: Object}}
 */
export function canPlaceGreatWork(nodeId, workId, discoveredNodes) {
  const work = GREAT_WORKS[workId];
  if (!work || !work.mapRequirement) {
    return { valid: false, reason: 'Invalid work or no map requirement' };
  }

  const node = discoveredNodes[nodeId];
  if (!node) {
    return { valid: false, reason: 'Tile not discovered' };
  }

  const coords = idToCoords(nodeId);
  if (!coords) {
    return { valid: false, reason: 'Invalid coordinates' };
  }

  // Check anchor requirement
  if (!meetsAnchorRequirement(node.biome, work.mapRequirement.anchor)) {
    return { valid: false, reason: 'Anchor biome mismatch' };
  }

  // Check nearby requirement
  const nearbyCheck = checkNearbyRequirement(
    coords.q,
    coords.r,
    work.mapRequirement.nearby,
    discoveredNodes
  );

  if (!nearbyCheck.meets) {
    return {
      valid: false,
      reason: 'Nearby requirement not met',
      nearbyCheck,
    };
  }

  return { valid: true, nearbyCheck };
}

// ===========================================
// FIND ALL VALID PLACEMENTS
// ===========================================

/**
 * Find all valid placement locations for a Great Work
 * @param {string} workId - The Great Work ID
 * @param {Object} discoveredNodes - Map of discovered tiles
 * @param {Set<string>} builtWorkNodeIds - Set of node IDs that already have Great Works
 * @returns {Array<{nodeId: string, nearbyCheck: Object}>}
 */
export function findValidPlacements(workId, discoveredNodes, builtWorkNodeIds = new Set()) {
  const work = GREAT_WORKS[workId];
  if (!work || !work.mapRequirement) return [];

  const validPlacements = [];

  // Pre-compute which biomes can be anchors
  const validAnchorBiomes = new Set();
  const anchorReq = work.mapRequirement.anchor;

  if (anchorReq?.biomes) {
    anchorReq.biomes.forEach(b => validAnchorBiomes.add(b));
  } else if (anchorReq?.category) {
    getBiomesMatchingRequirement(anchorReq.category).forEach(b => validAnchorBiomes.add(b));
  }

  // Check each discovered tile
  for (const [nodeId, node] of Object.entries(discoveredNodes)) {
    // Skip kingdom and already-used tiles
    if (nodeId === 'kingdom') continue;
    if (builtWorkNodeIds.has(nodeId)) continue;

    // Skip wonder site tiles (can't build Great Works on World Wonders)
    if (node.biome === 'wonderSite' || node.wonderSiteId) continue;

    // Skip the hub/kingdom tile at 0,0
    if (node.isHub) continue;

    // Quick biome check (skip full validation if anchor doesn't match)
    if (validAnchorBiomes.size > 0 && !validAnchorBiomes.has(node.biome)) {
      continue;
    }

    // Full validation
    const result = canPlaceGreatWork(nodeId, workId, discoveredNodes);
    if (result.valid) {
      validPlacements.push({
        nodeId,
        nearbyCheck: result.nearbyCheck,
      });
    }
  }

  return validPlacements;
}

/**
 * Get all valid placements for all Great Works (cached)
 * @param {Object} discoveredNodes - Map of discovered tiles
 * @param {Array<string>} builtWorkIds - Array of Great Work IDs already built
 * @param {Object} builtWorkLocations - Map of workId -> nodeId for built works
 * @returns {Object} Map of workId -> Array of valid placements
 */
export function getAllValidPlacements(discoveredNodes, builtWorkIds = [], builtWorkLocations = {}) {
  // Check cache
  const newCacheKey = generateCacheKey(discoveredNodes);
  if (validPlacementsCache && cacheKey === newCacheKey) {
    return validPlacementsCache;
  }

  // Build set of used node IDs
  const usedNodeIds = new Set(Object.values(builtWorkLocations));

  // Calculate valid placements for each unbuilt Great Work
  const allPlacements = {};

  for (const workId of Object.keys(GREAT_WORKS)) {
    // Skip already built works
    if (builtWorkIds.includes(workId)) {
      allPlacements[workId] = [];
      continue;
    }

    allPlacements[workId] = findValidPlacements(workId, discoveredNodes, usedNodeIds);
  }

  // Update cache
  validPlacementsCache = allPlacements;
  cacheKey = newCacheKey;

  return allPlacements;
}

/**
 * Check if a Great Work can be afforded
 * @param {string} workId - The Great Work ID
 * @param {Object} playerResources - Current resource amounts
 * @returns {{canAfford: boolean, missing: Object}}
 */
export function canAffordGreatWork(workId, playerResources) {
  const work = GREAT_WORKS[workId];
  if (!work) return { canAfford: false, missing: {} };

  const missing = {};
  let canAfford = true;

  for (const [resource, amount] of Object.entries(work.cost.resources || {})) {
    const have = playerResources[resource] || 0;
    if (have < amount) {
      canAfford = false;
      missing[resource] = amount - have;
    }
  }

  return { canAfford, missing };
}

/**
 * Get a summary of requirements for UI display
 * @param {string} workId - The Great Work ID
 * @returns {Object} Readable requirement summary
 */
export function getRequirementSummary(workId) {
  const work = GREAT_WORKS[workId];
  if (!work || !work.mapRequirement) return null;

  const { anchor, nearby } = work.mapRequirement;
  const summary = {
    anchor: '',
    nearby: '',
    connected: nearby?.connected || false,
  };

  // Anchor summary
  if (anchor?.biomes) {
    summary.anchor = `Place on: ${anchor.biomes.join(' or ')}`;
  } else if (anchor?.category) {
    const cats = Array.isArray(anchor.category) ? anchor.category : [anchor.category];
    summary.anchor = `Place on: ${cats.join(' or ')} tile`;
  }

  // Nearby summary
  if (nearby) {
    const count = nearby.minCount || 1;
    const range = nearby.range || 2;
    let what = 'tiles';

    if (nearby.anyBiome) {
      what = 'any tiles';
    } else if (nearby.category) {
      const cats = Array.isArray(nearby.category) ? nearby.category : [nearby.category];
      what = cats.join('/') + ' tiles';
    } else if (nearby.biomes) {
      what = nearby.biomes.join('/') + ' tiles';
    }

    summary.nearby = `${count}+ ${what} within ${range} hexes`;

    if (nearby.connected) {
      summary.nearby += ' (connected)';
    }

    if (nearby.requireDiversity) {
      summary.nearby += `, ${nearby.requireDiversity}+ different types`;
    }
  }

  return summary;
}

export default {
  clearGreatWorksCache,
  getTilesInRange,
  getConnectedTiles,
  meetsAnchorRequirement,
  checkNearbyRequirement,
  canPlaceGreatWork,
  findValidPlacements,
  getAllValidPlacements,
  canAffordGreatWork,
  getRequirementSummary,
};
